import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import VerdictChip from './components/VerdictChip';
import SectionsChangedCell from './components/SectionsChangedCell';
import { COMPARISON_COLUMN_WIDTH } from './constants';
import styles from './styles';
import { SnapshotComparisonDetailRow } from './types';

const scrollableCell = (value: string | null) => (
  <Box sx={styles.scrollableCell}>{value ?? ''}</Box>
);

const wrappedCell = (value: string | null) => <Box sx={styles.wrappedCell}>{value ?? ''}</Box>;

/* eslint-disable no-unused-vars */
type DetailColumnHandlers = {
  onVerdictChange: (id: number, value: boolean | null) => void;
  onCommentsChange: (id: number, value: string) => void;
  onCompare: (row: SnapshotComparisonDetailRow) => void;
  onOpenLetter: (row: SnapshotComparisonDetailRow) => void;
  onSave: (rowId: number) => void;
  savingRowId: number | null;
};
/* eslint-enable no-unused-vars */

const getSmeVerdictSelectValue = (verdict: boolean | null | undefined): string => {
  // MUI Select values are strings; map boolean/null to select options.
  if (verdict === true) {
    return 'true';
  }

  if (verdict === false) {
    return 'false';
  }

  return '';
};

const getSnapshotComparisonDetailColumns = ({
  onVerdictChange,
  onCommentsChange,
  onCompare,
  onOpenLetter,
  onSave,
  savingRowId
}: DetailColumnHandlers): GridColDef[] => [
  {
    field: 'comparison',
    headerName: 'Comparison',
    width: COMPARISON_COLUMN_WIDTH,
    minWidth: COMPARISON_COLUMN_WIDTH,
    headerAlign: 'center',
    sortable: false,
    cellClassName: 'comparison-cell',
    valueGetter: ({ row }) => `${row.compared_to_submission ?? ''} → ${row.submission ?? ''}`,
    renderCell: ({ value }) => wrappedCell(value as string | null)
  },
  {
    field: 'description',
    headerName: 'Description',
    width: 140,
    sortable: false
  },
  {
    field: 'ind_change',
    headerName: 'Indication Change',
    width: 120,
    sortable: false
  },
  {
    field: 'change_field',
    headerName: 'Change field',
    width: 300,
    sortable: false,
    renderCell: ({ value }) => scrollableCell(value as string | null)
  },
  {
    field: 'studies_results_delta',
    headerName: 'Studies / results delta',
    width: 200,
    sortable: false,
    renderCell: ({ value }) => scrollableCell(value as string | null)
  },
  {
    field: 'change_type',
    headerName: 'Change type',
    width: 150,
    sortable: false
  },
  {
    field: 'verdict',
    headerName: 'Verdict',
    width: 100,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: ({ value }) => <VerdictChip value={value as boolean | null} />
  },
  {
    field: 'tier',
    headerName: 'Tier',
    width: 110,
    sortable: false
  },
  {
    field: 'detail',
    headerName: 'Detail',
    width: 420,
    sortable: false,
    renderCell: ({ value }) => scrollableCell(value as string | null)
  },
  {
    field: 'letter',
    headerName: 'Letter',
    width: 100,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: ({ row }) => {
      const hasLetters = (row.letter_links?.length ?? 0) > 0;

      return (
        <Button
          variant='text'
          size='small'
          disabled={!hasLetters}
          sx={styles.linkButton}
          onClick={event => {
            event.stopPropagation();
            onOpenLetter(row as SnapshotComparisonDetailRow);
          }}>
          Letter
        </Button>
      );
    }
  },
  {
    field: 'compare',
    headerName: 'Compare',
    width: 110,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: ({ row }) => (
      <Button
        variant='text'
        size='small'
        sx={styles.linkButton}
        onClick={event => {
          event.stopPropagation();
          onCompare(row as SnapshotComparisonDetailRow);
        }}>
        Compare
      </Button>
    )
  },
  {
    field: 'section_changed',
    headerName: 'Sections Changed',
    width: 300,
    sortable: false,
    renderCell: ({ row }) => <SectionsChangedCell sections={row.section_changed ?? []} />
  },
  {
    field: 'sme_verdict',
    headerName: 'SME Verdict',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: ({ row }) => {
      const current = getSmeVerdictSelectValue(row.sme_verdict);
      return (
        <FormControl size='small' fullWidth>
          <Select
            value={current}
            displayEmpty
            onChange={event => {
              const next = event.target.value;
              onVerdictChange(row.id, next === '' ? null : next === 'true');
            }}>
            <MenuItem value=''>
              <em>Select</em>
            </MenuItem>
            <MenuItem value='true'>YES</MenuItem>
            <MenuItem value='false'>NO</MenuItem>
          </Select>
        </FormControl>
      );
    }
  },
  {
    field: 'sme_comments',
    headerName: 'SME Comments',
    width: 280,
    sortable: false,
    renderCell: ({ row }) => (
      <TextField
        size='small'
        fullWidth
        multiline
        maxRows={4}
        placeholder='Add comments'
        value={row.sme_comments ?? ''}
        onChange={event => onCommentsChange(row.id, event.target.value)}
      />
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: ({ row }) => (
      <Button
        variant='contained'
        size='small'
        disabled={savingRowId === row.id}
        onClick={() => onSave(row.id)}
        sx={styles.saveButton}>
        {savingRowId === row.id ? <CircularProgress size={18} color='inherit' /> : 'Save'}
      </Button>
    )
  }
];

export default getSnapshotComparisonDetailColumns;
